/**
 * Deployed ASSA WAVE contract addresses + minimal ABIs.
 *
 * Env-driven: VITE_ENV=mainnet → Base mainnet contracts;
 * VITE_ENV=testnet → Base Sepolia contracts.
 *
 * The investor-facing surface is split into two single-chain builds
 * (app.assawave.io + app-testnet.assawave.io) so an investor never has to
 * wonder which chain they're on.
 *
 * ⚠️ Addresses are filled AFTER deployment from onchain/deployments/<network>.json.
 *    Phase-1 contracts deploy to Base Sepolia first (audit gate), then mainnet.
 *    Until then they are the zero address and the UI shows a "not yet live" state.
 */
import type { Address } from "viem";
import { ADMIN_CHAIN_ID, ADMIN_EXPLORER, IS_MAINNET } from "./env";

const ZERO = "0x0000000000000000000000000000000000000000" as Address;

const CONTRACTS_MAINNET = {
  // Fill from onchain/deployments/base.json after Audit #1/#2 + mainnet deploy:
  assaToken: ZERO,
  tokenSale: ZERO,
  tokenVesting: ZERO,
  stakingLock: ZERO,
  bmeBurner: ZERO,
  kycRegistry: ZERO,
  treasury: ZERO,
  timelock: ZERO,
  usdc: "0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913" as Address, // canonical Base USDC
};

const CONTRACTS_TESTNET = {
  // Base Sepolia full fresh redeploy 2026-06-10 — v2 patched stack (P0+P1);
  // pre-handoff (deployer admin); onchain/deployments/baseSepolia.json:
  assaToken: "0x6139EB8724608f442B3A851eD3907D9B428129C0" as Address,
  tokenSale: "0xD532eee8d41A6f4F5e8Fd5c84B4AE5D5602B6674" as Address,
  tokenVesting: "0x4f4C873D5Eba3aD31E6633abd3D5F3A75fdcDEAA" as Address,
  stakingLock: "0xa4181Af9358FFD5a24F1100Bc930721336Fe4b3b" as Address,
  bmeBurner: ZERO, // not deployed on testnet (needs a DEX pool/router)
  kycRegistry: "0x1e5A8f22E4A01DEDb31e8075930cfc2d23755F3b" as Address,
  treasury: "0x08c6E2353899FABeDbCfF87e036e173DFeF6DFDf" as Address,
  timelock: "0x4cF8496E352682c760311C705fb724e65652796F" as Address,
  usdc: "0x55e27D3F68fCcf6e3810D7038002831E1CeC6439" as Address, // MockUSDC (6dec)
};

/** Active contract set — pinned by build env. */
export const CONTRACTS = IS_MAINNET ? CONTRACTS_MAINNET : CONTRACTS_TESTNET;

/** Active chain id, pinned by build. */
export const CHAIN_ID = ADMIN_CHAIN_ID;

/** Active block explorer base URL. */
export const EXPLORER = ADMIN_EXPLORER;

/** True once real addresses are wired in (zero address = contract not live yet). */
export const CONTRACTS_LIVE = CONTRACTS.assaToken !== ZERO;

// ===================== ASSAToken =====================

export const ASSATokenAbi = [
  { type: "function", name: "name", inputs: [], outputs: [{ type: "string" }], stateMutability: "view" },
  { type: "function", name: "symbol", inputs: [], outputs: [{ type: "string" }], stateMutability: "view" },
  { type: "function", name: "decimals", inputs: [], outputs: [{ type: "uint8" }], stateMutability: "view" },
  { type: "function", name: "totalSupply", inputs: [], outputs: [{ type: "uint256" }], stateMutability: "view" },
  { type: "function", name: "balanceOf", inputs: [{ type: "address" }], outputs: [{ type: "uint256" }], stateMutability: "view" },
  { type: "function", name: "allowance", inputs: [{ type: "address" }, { type: "address" }], outputs: [{ type: "uint256" }], stateMutability: "view" },
  { type: "function", name: "approve", inputs: [{ type: "address" }, { type: "uint256" }], outputs: [{ type: "bool" }], stateMutability: "nonpayable" },
  { type: "function", name: "transfer", inputs: [{ type: "address" }, { type: "uint256" }], outputs: [{ type: "bool" }], stateMutability: "nonpayable" },
  { type: "function", name: "CAP", inputs: [], outputs: [{ type: "uint256" }], stateMutability: "view" },
  { type: "function", name: "getVotes", inputs: [{ type: "address" }], outputs: [{ type: "uint256" }], stateMutability: "view" },
  { type: "function", name: "delegate", inputs: [{ type: "address" }], outputs: [], stateMutability: "nonpayable" },
] as const;

// ===================== TokenSale =====================

export const TokenSaleAbi = [
  // reads
  { type: "function", name: "assaToken", inputs: [], outputs: [{ type: "address" }], stateMutability: "view" },
  { type: "function", name: "usdcToken", inputs: [], outputs: [{ type: "address" }], stateMutability: "view" },
  { type: "function", name: "getRoundCount", inputs: [], outputs: [{ type: "uint256" }], stateMutability: "view" },
  {
    type: "function",
    name: "getRound",
    inputs: [{ type: "uint256" }],
    outputs: [
      {
        type: "tuple",
        components: [
          { name: "name", type: "string" },
          { name: "priceUsdc", type: "uint256" },
          { name: "hardCapTokens", type: "uint128" },
          { name: "soldTokens", type: "uint128" },
          { name: "startTime", type: "uint64" },
          { name: "endTime", type: "uint64" },
          { name: "cliffSeconds", type: "uint32" },
          { name: "vestSeconds", type: "uint32" },
          { name: "tgeBps", type: "uint16" },
          { name: "active", type: "bool" },
        ],
      },
    ],
    stateMutability: "view",
  },
  {
    type: "function",
    name: "allocations",
    inputs: [{ type: "uint256" }, { type: "address" }],
    outputs: [
      { name: "totalAllocated", type: "uint128" },
      { name: "claimed", type: "uint128" },
      { name: "startTime", type: "uint64" },
      { name: "cliffSeconds", type: "uint32" },
      { name: "vestSeconds", type: "uint32" },
      { name: "tgeBps", type: "uint16" },
    ],
    stateMutability: "view",
  },
  { type: "function", name: "whitelist", inputs: [{ type: "uint256" }, { type: "address" }], outputs: [{ type: "bool" }], stateMutability: "view" },
  { type: "function", name: "claimable", inputs: [{ name: "roundId", type: "uint256" }, { name: "user", type: "address" }], outputs: [{ type: "uint256" }], stateMutability: "view" },
  { type: "function", name: "quoteUsdc", inputs: [{ type: "uint256" }, { type: "uint256" }], outputs: [{ type: "uint256" }], stateMutability: "view" },
  // investor writes
  { type: "function", name: "purchase", inputs: [{ name: "roundId", type: "uint256" }, { name: "assaAmount", type: "uint256" }], outputs: [], stateMutability: "nonpayable" },
  { type: "function", name: "claim", inputs: [{ name: "roundId", type: "uint256" }], outputs: [], stateMutability: "nonpayable" },
  // events
  { type: "event", name: "Purchased", inputs: [
    { type: "uint256", indexed: true },
    { type: "address", indexed: true },
    { type: "uint256" },
    { type: "uint256" },
  ] },
  { type: "event", name: "Claimed", inputs: [
    { type: "uint256", indexed: true },
    { type: "address", indexed: true },
    { type: "uint256" },
  ] },
] as const;

// ===================== TokenVesting =====================

export const TokenVestingAbi = [
  {
    type: "function",
    name: "schedules",
    inputs: [{ type: "uint256" }],
    outputs: [
      { name: "beneficiary", type: "address" },
      { name: "total", type: "uint256" },
      { name: "claimed", type: "uint256" },
      { name: "start", type: "uint256" },
      { name: "cliff", type: "uint256" },
      { name: "duration", type: "uint256" },
      { name: "tgeBps", type: "uint16" },
      { name: "category", type: "uint8" },
      { name: "revocable", type: "bool" },
      { name: "revoked", type: "bool" },
    ],
    stateMutability: "view",
  },
  { type: "function", name: "scheduleIdsOf", inputs: [{ type: "address" }], outputs: [{ type: "uint256[]" }], stateMutability: "view" },
  { type: "function", name: "scheduleCountOf", inputs: [{ type: "address" }], outputs: [{ type: "uint256" }], stateMutability: "view" },
  { type: "function", name: "releasable", inputs: [{ type: "uint256" }], outputs: [{ type: "uint256" }], stateMutability: "view" },
  { type: "function", name: "vestedOf", inputs: [{ type: "uint256" }], outputs: [{ type: "uint256" }], stateMutability: "view" },
  { type: "function", name: "release", inputs: [{ type: "uint256" }], outputs: [], stateMutability: "nonpayable" },
  { type: "function", name: "releaseAll", inputs: [], outputs: [], stateMutability: "nonpayable" },
  { type: "function", name: "getScheduleCount", inputs: [], outputs: [{ type: "uint256" }], stateMutability: "view" },
] as const;

// ===================== StakingLock (veASSA) =====================

export const StakingLockAbi = [
  {
    type: "function",
    name: "locks",
    inputs: [{ type: "address" }],
    outputs: [
      { name: "amount", type: "uint128" },
      { name: "start", type: "uint64" },
      { name: "end", type: "uint64" },
    ],
    stateMutability: "view",
  },
  { type: "function", name: "votingPower", inputs: [{ type: "address" }], outputs: [{ type: "uint256" }], stateMutability: "view" },
  { type: "function", name: "votingPowerAt", inputs: [{ type: "address" }, { type: "uint256" }], outputs: [{ type: "uint256" }], stateMutability: "view" },
  { type: "function", name: "tierOf", inputs: [{ type: "address" }], outputs: [{ type: "uint8" }], stateMutability: "view" },
  { type: "function", name: "tierWeight", inputs: [{ type: "address" }], outputs: [{ type: "uint256" }], stateMutability: "view" },
  { type: "function", name: "totalLocked", inputs: [], outputs: [{ type: "uint256" }], stateMutability: "view" },
  { type: "function", name: "MAX_LOCK_DURATION", inputs: [], outputs: [{ type: "uint256" }], stateMutability: "view" },
  { type: "function", name: "MIN_LOCK_DURATION", inputs: [], outputs: [{ type: "uint256" }], stateMutability: "view" },
  // writes
  { type: "function", name: "lock", inputs: [{ name: "amount", type: "uint256" }, { name: "duration", type: "uint256" }], outputs: [], stateMutability: "nonpayable" },
  { type: "function", name: "increaseAmount", inputs: [{ name: "amount", type: "uint256" }], outputs: [], stateMutability: "nonpayable" },
  { type: "function", name: "increaseUnlockTime", inputs: [{ name: "duration", type: "uint256" }], outputs: [], stateMutability: "nonpayable" },
  { type: "function", name: "withdraw", inputs: [], outputs: [], stateMutability: "nonpayable" },
] as const;

// ===================== KYCRegistry =====================

export const KYCRegistryAbi = [
  { type: "function", name: "isKYCed", inputs: [{ type: "address" }], outputs: [{ type: "bool" }], stateMutability: "view" },
  { type: "function", name: "kycedCount", inputs: [], outputs: [{ type: "uint256" }], stateMutability: "view" },
] as const;

// ===================== USDC =====================

export const USDCAbi = [
  { type: "function", name: "name", inputs: [], outputs: [{ type: "string" }], stateMutability: "view" },
  { type: "function", name: "symbol", inputs: [], outputs: [{ type: "string" }], stateMutability: "view" },
  { type: "function", name: "decimals", inputs: [], outputs: [{ type: "uint8" }], stateMutability: "view" },
  { type: "function", name: "balanceOf", inputs: [{ type: "address" }], outputs: [{ type: "uint256" }], stateMutability: "view" },
  { type: "function", name: "allowance", inputs: [{ type: "address" }, { type: "address" }], outputs: [{ type: "uint256" }], stateMutability: "view" },
  { type: "function", name: "approve", inputs: [{ name: "spender", type: "address" }, { name: "amount", type: "uint256" }], outputs: [{ type: "bool" }], stateMutability: "nonpayable" },
] as const;
